
import React, { useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog.tsx';
import { useToast } from '@/hooks/use-toast.ts';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext.tsx';
import { type PatientEnroller } from '@/hooks/pa/usePatientEnrollers.ts';
import { serviceFactory } from '@/services/core/serviceFactory.ts';
import { PatientEnrollerService } from '@/services/features/PatientEnrollerService.ts';

interface DeletePatientEnrollerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patientEnroller: PatientEnroller | null;
  onDelete: () => void;
}

const DeletePatientEnrollerDialog: React.FC<DeletePatientEnrollerDialogProps> = ({
  open,
  onOpenChange,
  patientEnroller,
  onDelete,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const { user, token, hasuraToken } = useAuth();

  const handleDelete = async () => {
    if (!patientEnroller || !user?.id || !token) return;

    setIsLoading(true);
    try {
      // Update service context with current auth info - FIXED: Use correct token types
      serviceFactory.updateGlobalContext({
        hasuraToken: hasuraToken,
        userId: user.id,
        token: token,
        userType: user.type
      });

      const patientEnrollerService = serviceFactory.getService(PatientEnrollerService);
      const result = await patientEnrollerService.deletePatientEnroller(patientEnroller.patient_enroller_id);
      
      if (result.success) {
        toast({
          title: "Success",
          description: "Patient Enrollment Specialist deleted successfully!",
        });
        onDelete();
        onOpenChange(false);
      } else {
        throw new Error(result.message || 'Failed to delete patient enrollment specialist');
      }
    } catch (error: any) {
      console.error('Error deleting patient enrollment specialist:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to delete patient enrollment specialist.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Patient Enroller</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete {patientEnroller?.full_name}? This action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isLoading}>Cancel</AlertDialogCancel>
          <AlertDialogAction disabled={isLoading} onClick={handleDelete}>
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Deleting...
              </>
            ) : (
              'Delete'
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default DeletePatientEnrollerDialog;
